import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    Megaphone,
    Plus,
    Trash2,
    Send,
    Clock,
    CheckCircle,
    Sparkles,
    Zap,
    Loader2,
    Pencil,
    XCircle,
    AlertTriangle,
    FileText,
    Users,
    Heart,
    UserPlus,
    UserMinus,
    UserX,
    Phone,
    Save,
    Upload,
    Rocket,
} from "lucide-react";
import CustomerProfileSheet from "@/components/CustomerProfileSheet";

const STATUS_COLORS = {
    pending_approval: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
    draft: "bg-white/5 text-white/60 border-white/10",
    scheduled: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    approved: "bg-electric/15 text-electric border-electric/30",
    active: "bg-electric/20 text-electric border-electric/40",
    completed: "bg-green-500/15 text-green-400 border-green-500/30",
    paused: "bg-orange-500/15 text-orange-400 border-orange-500/30",
};

const CampaignManager = () => {
    const { token } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [bases, setBases] = useState([]);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create/edit campaign state
    const [showDialog, setShowDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        name: "",
        description: "",
        channel: "sms",
        target_audience: "",
        message_template: "",
        customer_base_id: "",
        scheduled_at: "",
    });

    // Generate SMS state
    const [genSource, setGenSource] = useState("custom");
    const [genArticle, setGenArticle] = useState("");
    const [genFixtureId, setGenFixtureId] = useState("");
    const [genLeague, setGenLeague] = useState("eng.1");
    const [genCustom, setGenCustom] = useState("");
    const [generating, setGenerating] = useState(false);

    // Audience picker state
    const [audMode, setAudMode] = useState("base"); // "base" | "segment"
    const [segKind, setSegKind] = useState("team"); // team | joined_within | inactive | never_engaged
    const [segTeam, setSegTeam] = useState("");
    const [segDays, setSegDays] = useState(7);
    const [audPhones, setAudPhones] = useState([]);
    const [audCount, setAudCount] = useState(0);
    const [audLabel, setAudLabel] = useState("");
    const [audLoading, setAudLoading] = useState(false);
    const [teams, setTeams] = useState([]);
    const [saveBaseName, setSaveBaseName] = useState("");
    const [profilePhone, setProfilePhone] = useState(null);

    // CSV-upload-as-audience state
    const [csvFile, setCsvFile] = useState(null);
    const [csvName, setCsvName] = useState("");
    const [csvRegion, setCsvRegion] = useState("");
    const [csvUploading, setCsvUploading] = useState(false);

    const handleCsvUploadAsAudience = async () => {
        if (!csvFile) { toast.error("Pick a CSV file"); return; }
        const name = (csvName.trim() || `Uploaded ${new Date().toLocaleString("en-GB")}`).slice(0, 80);
        const fd = new FormData();
        fd.append("name", name);
        fd.append("description", `Uploaded for campaign "${form.name || "(unnamed)"}"`);
        if (csvRegion.trim()) fd.append("region", csvRegion.trim());
        fd.append("file", csvFile);
        setCsvUploading(true);
        try {
            const { data } = await axios.post(`${API}/customer-bases/upload-csv`, fd, {
                headers: { ...authH(), "Content-Type": "multipart/form-data" },
            });
            toast.success(`Imported ${data.count} numbers as "${data.name}"`);
            await fetchAll();
            setForm((p) => ({ ...p, customer_base_id: data.id }));
            setAudMode("base");
            setCsvFile(null); setCsvName(""); setCsvRegion("");
            previewAudience({ mode: "base", baseId: data.id });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally { setCsvUploading(false); }
    };

    const authH = () => ({ Authorization: `Bearer ${token}` });

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [c, cb, a, t] = await Promise.all([
                axios.get(`${API}/campaigns?limit=100`, { headers: authH() }),
                axios.get(`${API}/customer-bases`, { headers: authH() }),
                axios.get(`${API}/content?limit=30&status=published`, { headers: authH() }).catch(() => ({ data: [] })),
                axios.get(`${API}/audiences/teams`, { headers: authH() }).catch(() => ({ data: [] })),
            ]);
            setCampaigns(c.data || []);
            setBases(cb.data || []);
            setArticles(a.data || []);
            setTeams(t.data || []);
        } catch { toast.error("Failed to load data"); }
        finally { setLoading(false); }
    };

    const previewAudience = async (override = {}) => {
        const mode = override.mode || audMode;
        const kind = override.kind || segKind;
        const baseId = override.baseId !== undefined ? override.baseId : form.customer_base_id;
        const team = override.team !== undefined ? override.team : segTeam;
        const days = override.days !== undefined ? override.days : segDays;
        if (mode === "base" && !baseId) { setAudPhones([]); setAudCount(0); setAudLabel(""); return; }
        if (mode === "segment" && kind === "team" && !team) { setAudPhones([]); setAudCount(0); setAudLabel(""); return; }
        setAudLoading(true);
        try {
            const body = mode === "base" ? { kind: "base", base_id: baseId } : { kind, team, days };
            const { data } = await axios.post(`${API}/audiences/preview`, body, { headers: authH() });
            setAudPhones(data.phones || []);
            setAudCount(data.count || 0);
            setAudLabel(data.label || "");
        } catch (e) {
            toast.error("Audience preview failed");
            setAudPhones([]); setAudCount(0);
        } finally { setAudLoading(false); }
    };

    const saveAudienceAsBase = async () => {
        if (!saveBaseName.trim()) { toast.error("Give the base a name"); return; }
        if (audPhones.length === 0) { toast.error("Preview an audience first"); return; }
        try {
            const { data } = await axios.post(
                `${API}/audiences/save-as-base`,
                { name: saveBaseName.trim(), description: audLabel, phone_numbers: audPhones },
                { headers: authH() }
            );
            toast.success(`Saved "${data.name}" — ${data.count} numbers`);
            // Refresh the bases list and select the new one + switch to "base" mode
            await fetchAll();
            setForm((p) => ({ ...p, customer_base_id: data.id }));
            setAudMode("base");
            setSaveBaseName("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const resetForm = () => {
        setForm({
            name: "", description: "", channel: "sms", target_audience: "",
            message_template: "", customer_base_id: "", scheduled_at: "",
        });
        setEditingId(null);
        setGenSource("custom");
        setGenArticle(""); setGenFixtureId(""); setGenLeague("eng.1"); setGenCustom("");
        setAudMode("base"); setSegKind("team"); setSegTeam(""); setSegDays(7);
        setAudPhones([]); setAudCount(0); setAudLabel(""); setSaveBaseName("");
    };

    const openNew = () => { resetForm(); setShowDialog(true); };

    const openEdit = (c) => {
        setEditingId(c.id);
        setForm({
            name: c.name || "",
            description: c.description || "",
            channel: c.channel || "sms",
            target_audience: c.target_audience || "",
            message_template: c.message_template || "",
            customer_base_id: c.customer_base_id || "",
            scheduled_at: c.scheduled_at ? new Date(c.scheduled_at).toISOString().slice(0, 16) : "",
        });
        setAudMode("base");
        setShowDialog(true);
        if (c.customer_base_id) previewAudience({ mode: "base", baseId: c.customer_base_id });
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const payload = { source_type: genSource, target_chars: 160 };
            if (genSource === "article") payload.source_id = genArticle;
            else if (genSource === "fixture") {
                payload.source_id = genFixtureId;
                payload.league = genLeague;
                payload.sport = "soccer";
            }
            else if (genSource === "result") {
                payload.source_id = genFixtureId;
                if (genLeague) payload.league = genLeague;
                payload.sport = "soccer";
            }
            else if (genSource === "custom") payload.source_text = genCustom;

            const { data } = await axios.post(`${API}/campaigns/generate-sms`, payload, { headers: authH() });
            setForm((p) => ({ ...p, message_template: data.message }));
            toast.success(`Generated ${data.length} chars`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Generation failed");
        } finally { setGenerating(false); }
    };

    const handleSave = async () => {
        if (!form.name || !form.message_template) {
            toast.error("Name and message required");
            return;
        }
        const payload = {
            ...form,
            customer_base_id: form.customer_base_id || null,
            scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        };
        try {
            if (editingId) {
                await axios.put(`${API}/campaigns/${editingId}`, payload, { headers: authH() });
                toast.success("Updated");
            } else {
                await axios.post(`${API}/campaigns`, payload, { headers: authH() });
                toast.success("Created");
            }
            setShowDialog(false);
            resetForm();
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const handleSaveAndSchedule = async () => {
        // Smart-segment mode: save the resolved phones as a fresh base, then create the campaign.
        if (!form.name || !form.message_template) {
            toast.error("Name and message required");
            return;
        }
        if (audPhones.length === 0) {
            toast.error("Preview an audience first");
            return;
        }
        const baseName = (saveBaseName.trim() || `${form.name} audience`).slice(0, 80);
        setAudLoading(true);
        try {
            const { data: base } = await axios.post(
                `${API}/audiences/save-as-base`,
                { name: baseName, description: audLabel, phone_numbers: audPhones },
                { headers: authH() }
            );
            await axios.post(`${API}/campaigns`, {
                ...form,
                customer_base_id: base.id,
                scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
            }, { headers: authH() });
            toast.success(`Saved base "${base.name}" (${base.count}) + created campaign`);
            setShowDialog(false);
            resetForm();
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save & schedule failed");
        } finally { setAudLoading(false); }
    };

    const handleApprove = async (id) => {
        try {
            await axios.post(`${API}/campaigns/${id}/approve`, {}, { headers: authH() });
            toast.success("Approved");
            fetchAll();
        } catch { toast.error("Approve failed"); }
    };

    const handleSendNow = async (id) => {
        if (!window.confirm("Dispatch this SMS campaign now? (MOCK send — no real SMS yet)")) return;
        try {
            const { data } = await axios.post(`${API}/campaigns/${id}/send-now`, {}, { headers: authH() });
            toast.success(`Dispatched: ${data.sent} sent, ${data.delivered} delivered (MOCK)`);
            fetchAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    };

    const handleReject = async (id) => {
        if (!window.confirm("Reject (delete) this pending campaign?")) return;
        try {
            await axios.delete(`${API}/campaigns/${id}`, { headers: authH() });
            toast.success("Rejected");
            fetchAll();
        } catch { toast.error("Failed"); }
    };

    const pending = campaigns.filter((c) => c.status === "pending_approval");
    const rest = campaigns.filter((c) => c.status !== "pending_approval");

    return (
        <AdminLayout title="Campaigns">
            <div className="space-y-6" data-testid="campaign-manager-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Megaphone className="h-6 w-6 text-electric" />
                        <div>
                            <h1 className="text-2xl font-heading font-bold text-white">SMS Campaigns</h1>
                            <p className="text-white/50 text-sm">
                                Push SMS to customer bases. Generate copy with AI or write your own. Schedule or send now. <span className="text-yellow-400">Telecom send is MOCKED for now.</span>
                            </p>
                        </div>
                    </div>
                    <Button onClick={openNew} className="bg-electric hover:bg-vodaredDark text-white" data-testid="new-campaign-btn">
                        <Plus className="h-4 w-4 mr-2" /> New Campaign
                    </Button>
                </div>

                {/* Pending Approval (auto-drafted) */}
                {pending.length > 0 && (
                    <Card className="bg-yellow-950/20 border-yellow-500/30 p-5" data-testid="pending-approval-section">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="h-5 w-5 text-yellow-400" />
                            <h2 className="font-heading font-bold text-yellow-300 text-sm uppercase tracking-wider">
                                Pending Approval — Auto-drafted from fixtures
                            </h2>
                            <Badge className="bg-yellow-500/30 text-yellow-100 border-0">
                                {pending.length}
                            </Badge>
                        </div>
                        <p className="text-yellow-200/70 text-xs mb-4">
                            These SMS campaigns were auto-generated when fixtures were detected 90–180 minutes away. Review and approve, then they'll dispatch 15 minutes before kickoff.
                        </p>
                        <div className="space-y-3">
                            {pending.map((c, i) => (
                                <PendingCard
                                    key={c.id}
                                    c={c}
                                    idx={i}
                                    onApprove={() => handleApprove(c.id)}
                                    onReject={() => handleReject(c.id)}
                                    onEdit={() => openEdit(c)}
                                />
                            ))}
                        </div>
                    </Card>
                )}

                {/* All campaigns */}
                <Card className="bg-paper border-border overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center text-white/40">Loading...</div>
                    ) : rest.length === 0 ? (
                        <div className="p-12 text-center">
                            <Megaphone className="h-12 w-12 text-white/20 mx-auto mb-3" />
                            <p className="text-white/60">No campaigns yet.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-subtle border-b border-border">
                                    <tr className="text-left text-white/60 text-xs uppercase">
                                        <th className="px-4 py-3">Name</th>
                                        <th className="px-4 py-3">Message</th>
                                        <th className="px-4 py-3">Recipients</th>
                                        <th className="px-4 py-3">Schedule</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rest.map((c, idx) => (
                                        <tr key={c.id} className="border-b border-border/40 hover:bg-subtle/50" data-testid={`campaign-row-${idx}`}>
                                            <td className="px-4 py-3 text-white font-medium max-w-[220px] truncate">{c.name}</td>
                                            <td className="px-4 py-3 text-white/70 text-xs max-w-[340px] truncate">
                                                {c.message_template}
                                            </td>
                                            <td className="px-4 py-3 text-white/60 text-xs">
                                                {c.customer_base_name ? (
                                                    <>
                                                        <div>{c.customer_base_name}</div>
                                                        <div className="text-white/40 text-[10px]">{c.recipient_count} nos.</div>
                                                    </>
                                                ) : <span className="text-white/30">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-white/60 text-xs">
                                                {c.scheduled_at ? new Date(c.scheduled_at).toLocaleString("en-GB", { timeZone: "Africa/Dar_es_Salaam", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) + " EAT" : "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Badge variant="outline" className={`${STATUS_COLORS[c.status] || STATUS_COLORS.draft} text-xs`}>
                                                    {c.status}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-right whitespace-nowrap">
                                                {c.status !== "completed" && (
                                                    <>
                                                        <Button size="sm" variant="ghost" onClick={() => handleSendNow(c.id)}
                                                            className="text-electric hover:bg-electric/10" title="Send now" data-testid={`send-now-${idx}`}>
                                                            <Send className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)}
                                                            className="text-white/70 hover:text-electric" title="Edit" data-testid={`edit-campaign-${idx}`}>
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </>
                                                )}
                                                <Button size="sm" variant="ghost" onClick={() => handleReject(c.id)}
                                                    className="text-white/70 hover:text-red-400" title="Delete" data-testid={`delete-campaign-${idx}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="bg-paper border-border text-white max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading">
                            {editingId ? "Edit Campaign" : "New SMS Campaign"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                        <div className="md:col-span-2">
                            <Label className="text-white/70">Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="bg-subtle border-border text-white" placeholder="e.g. Arsenal vs Man City preview blast"
                                data-testid="campaign-name" />
                        </div>

                        {/* Generate with AI */}
                        <div className="md:col-span-2 p-3 rounded border border-electric/30 bg-electric/5">
                            <div className="flex items-center gap-2 mb-2">
                                <Zap className="h-4 w-4 text-electric" />
                                <span className="text-electric text-xs font-heading font-bold uppercase tracking-wider">
                                    Auto-Create with AI (160 chars)
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <Select value={genSource} onValueChange={setGenSource}>
                                    <SelectTrigger className="bg-subtle border-border h-9"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="custom">Custom prompt</SelectItem>
                                        <SelectItem value="article">From article (News Feed)</SelectItem>
                                        <SelectItem value="fixture">From fixture (upcoming)</SelectItem>
                                        <SelectItem value="result">From result (finished match)</SelectItem>
                                    </SelectContent>
                                </Select>
                                {genSource === "article" && (
                                    <Select value={genArticle} onValueChange={setGenArticle}>
                                        <SelectTrigger className="bg-subtle border-border h-9 md:col-span-2">
                                            <SelectValue placeholder="Pick article" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {articles.slice(0, 25).map((a) => (
                                                <SelectItem key={a.id} value={a.id}>
                                                    {(a.title_sw || a.title).slice(0, 60)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                {genSource === "fixture" && (
                                    <>
                                        <Input value={genFixtureId} onChange={(e) => setGenFixtureId(e.target.value)}
                                            placeholder="ESPN event id (e.g. 740923)" className="bg-subtle border-border text-white h-9" />
                                        <Input value={genLeague} onChange={(e) => setGenLeague(e.target.value)}
                                            placeholder="eng.1" className="bg-subtle border-border text-white h-9" />
                                    </>
                                )}
                                {genSource === "result" && (
                                    <>
                                        <Input value={genFixtureId} onChange={(e) => setGenFixtureId(e.target.value)}
                                            placeholder="Match ID (ESPN event or internal)" className="bg-subtle border-border text-white h-9" />
                                        <Input value={genLeague} onChange={(e) => setGenLeague(e.target.value)}
                                            placeholder="eng.1 (for ESPN; blank for internal)" className="bg-subtle border-border text-white h-9" />
                                    </>
                                )}
                                {genSource === "custom" && (
                                    <Textarea value={genCustom} onChange={(e) => setGenCustom(e.target.value)}
                                        placeholder="Prompt / context for the SMS (Swahili or English)"
                                        className="bg-subtle border-border text-white h-9 md:col-span-2" />
                                )}
                            </div>
                            <Button onClick={handleGenerate} disabled={generating}
                                size="sm" className="mt-2 bg-electric hover:bg-vodaredDark text-white"
                                data-testid="generate-sms-btn">
                                {generating ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating…</> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Generate SMS</>}
                            </Button>
                        </div>

                        <div className="md:col-span-2">
                            <Label className="text-white/70">Message (160 char SMS)</Label>
                            <Textarea value={form.message_template}
                                onChange={(e) => setForm({ ...form, message_template: e.target.value })}
                                className="bg-subtle border-border text-white font-mono h-28"
                                maxLength={480}
                                data-testid="campaign-message" />
                            <div className="flex justify-between text-xs mt-1">
                                <span className="text-white/50">
                                    {form.message_template.length} / 160 chars
                                    {form.message_template.length > 160 && <span className="text-yellow-400 ml-2">Will split into {Math.ceil(form.message_template.length / 160)} SMS</span>}
                                </span>
                            </div>
                        </div>

                        <div data-testid="audience-picker">
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-white/70">Audience</Label>
                                <div className="flex gap-1 bg-obsidian rounded-sm p-0.5">
                                    <button
                                        type="button"
                                        onClick={() => { setAudMode("base"); setAudPhones([]); setAudCount(0); setAudLabel(""); if (form.customer_base_id) previewAudience({ mode: "base" }); }}
                                        className={`px-3 py-1 text-xs rounded-sm transition-colors ${audMode === "base" ? "bg-electric text-white" : "text-white/60 hover:text-white"}`}
                                        data-testid="audience-tab-base"
                                    >
                                        <Users className="h-3 w-3 inline mr-1" /> Customer base
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setAudMode("segment"); setAudPhones([]); setAudCount(0); setAudLabel(""); setForm((p) => ({ ...p, customer_base_id: "" })); }}
                                        className={`px-3 py-1 text-xs rounded-sm transition-colors ${audMode === "segment" ? "bg-electric text-white" : "text-white/60 hover:text-white"}`}
                                        data-testid="audience-tab-segment"
                                    >
                                        <Sparkles className="h-3 w-3 inline mr-1" /> Smart segment
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setAudMode("upload"); setAudPhones([]); setAudCount(0); setAudLabel(""); setForm((p) => ({ ...p, customer_base_id: "" })); }}
                                        className={`px-3 py-1 text-xs rounded-sm transition-colors ${audMode === "upload" ? "bg-electric text-white" : "text-white/60 hover:text-white"}`}
                                        data-testid="audience-tab-upload"
                                    >
                                        <Upload className="h-3 w-3 inline mr-1" /> Upload CSV
                                    </button>
                                </div>
                            </div>

                            {audMode === "base" && (
                                <div className="space-y-2">
                                    <Select
                                        value={form.customer_base_id}
                                        onValueChange={(v) => {
                                            setForm({ ...form, customer_base_id: v });
                                            previewAudience({ mode: "base", baseId: v });
                                        }}
                                    >
                                        <SelectTrigger className="bg-subtle border-border text-white" data-testid="campaign-base-select">
                                            <SelectValue placeholder="Pick a customer base" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {bases.map((b) => (
                                                <SelectItem key={b.id} value={b.id}>{b.name} ({b.count})</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {bases.length === 0 && (
                                        <p className="text-yellow-400 text-xs">
                                            No bases yet — switch to <span className="underline">Smart segment</span> or
                                            <a href="/admin/customer-bases" className="underline ml-1">create one</a>.
                                        </p>
                                    )}
                                </div>
                            )}

                            {audMode === "segment" && (
                                <div className="space-y-3 bg-obsidian border border-border rounded-sm p-3">
                                    <div className="grid grid-cols-2 gap-2" data-testid="segment-types">
                                        <SegmentTile id="team" current={segKind} onClick={setSegKind} icon={Heart} label="Team fans" hint="e.g. Arsenal supporters" />
                                        <SegmentTile id="joined_within" current={segKind} onClick={setSegKind} icon={UserPlus} label="Recent joiners" hint="Joined in last X days" />
                                        <SegmentTile id="inactive" current={segKind} onClick={setSegKind} icon={UserMinus} label="Inactive" hint="No SMS in X days" />
                                        <SegmentTile id="never_engaged" current={segKind} onClick={setSegKind} icon={UserX} label="Never engaged" hint="Subscribed but silent" />
                                    </div>

                                    {segKind === "team" && (
                                        <div>
                                            <Label className="text-white/70 text-xs">Team</Label>
                                            <Select value={segTeam} onValueChange={setSegTeam}>
                                                <SelectTrigger className="bg-subtle border-border text-white" data-testid="segment-team-select">
                                                    <SelectValue placeholder="Pick a team with active subscribers" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {teams.map((t) => (
                                                        <SelectItem key={`${t.team}-${t.team_normalized}`} value={t.team}>
                                                            {t.team} <span className="text-white/40">· {t.count} fans</span>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className="text-white/40 text-[10px] mt-1">
                                                Type-in or pick from teams with active SMS subscriptions.
                                            </p>
                                        </div>
                                    )}

                                    {(segKind === "joined_within" || segKind === "inactive") && (
                                        <div>
                                            <Label className="text-white/70 text-xs">Window (days)</Label>
                                            <div className="flex gap-1 mt-1" data-testid="segment-days">
                                                {(segKind === "joined_within" ? [7, 30, 60] : [30, 60, 90]).map((d) => (
                                                    <button
                                                        type="button"
                                                        key={d}
                                                        onClick={() => setSegDays(d)}
                                                        className={`px-3 py-1.5 text-xs rounded-sm border ${segDays === d ? "bg-electric border-electric text-white" : "bg-subtle border-border text-white/70 hover:text-white"}`}
                                                        data-testid={`segment-days-${d}`}
                                                    >
                                                        {d} days
                                                    </button>
                                                ))}
                                                <Input
                                                    type="number"
                                                    value={segDays}
                                                    onChange={(e) => setSegDays(parseInt(e.target.value || "0", 10))}
                                                    className="w-20 bg-subtle border-border text-white text-xs"
                                                    min={1}
                                                    max={365}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => previewAudience()}
                                            disabled={audLoading}
                                            className="bg-electric hover:bg-electric/90"
                                            data-testid="segment-preview-btn"
                                        >
                                            {audLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                                            Preview audience
                                        </Button>
                                        {audPhones.length > 0 && (
                                            <div className="flex items-center gap-1.5 flex-1">
                                                <Input
                                                    placeholder='Save as base e.g. "Arsenal fans"'
                                                    value={saveBaseName}
                                                    onChange={(e) => setSaveBaseName(e.target.value)}
                                                    className="bg-subtle border-border text-white text-xs h-8"
                                                    data-testid="save-base-name"
                                                />
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={saveAudienceAsBase}
                                                    className="h-8 text-xs"
                                                    data-testid="save-base-btn"
                                                >
                                                    <Save className="h-3 w-3 mr-1" /> Save
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {audMode === "upload" && (
                                <div className="space-y-3 bg-obsidian border border-border rounded-sm p-3" data-testid="upload-csv-block">
                                    <div>
                                        <Label className="text-white/70 text-xs">Base name (auto-suggested)</Label>
                                        <Input
                                            value={csvName}
                                            onChange={(e) => setCsvName(e.target.value)}
                                            placeholder='e.g. "Promo blast Mar 2026"'
                                            className="bg-subtle border-border text-white text-xs"
                                            data-testid="campaign-csv-name"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-white/70 text-xs">CSV file</Label>
                                        <Input
                                            type="file"
                                            accept=".csv,.txt,.tsv"
                                            onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                                            className="bg-subtle border-border text-white text-xs file:text-white file:bg-electric file:border-0 file:px-2 file:py-1 file:rounded-sm file:mr-3"
                                            data-testid="campaign-csv-file"
                                        />
                                        <p className="text-white/40 text-[10px] mt-1">
                                            Auto-detects header (looks for <span className="text-electric">phone / msisdn / mobile</span>) or scans every cell for valid numbers.
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-white/70 text-xs">Region tag (optional)</Label>
                                        <Input
                                            value={csvRegion}
                                            onChange={(e) => setCsvRegion(e.target.value)}
                                            placeholder="e.g. Mwanza, Arusha, Mbeya"
                                            className="bg-subtle border-border text-white text-xs"
                                            data-testid="campaign-csv-region"
                                        />
                                        <p className="text-white/40 text-[10px] mt-1">
                                            Tag every phone with a region (a CSV <span className="text-electric">region</span> column overrides this per row).
                                        </p>
                                    </div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={handleCsvUploadAsAudience}
                                        disabled={csvUploading || !csvFile}
                                        className="bg-electric hover:bg-electric/90"
                                        data-testid="campaign-csv-upload-btn"
                                    >
                                        {csvUploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                                        Upload &amp; use as audience
                                    </Button>
                                </div>
                            )}

                            {/* Audience phone preview */}
                            {(audLabel || audCount > 0) && (
                                <div className="mt-2 bg-obsidian border border-border rounded-sm p-3" data-testid="audience-preview">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <Phone className="h-3.5 w-3.5 text-electric" />
                                            <span className="text-white font-bold text-xs">{audLabel}</span>
                                            <Badge className="bg-electric/15 text-electric border-0 text-[10px]">{audCount} numbers</Badge>
                                        </div>
                                    </div>
                                    {audPhones.length === 0 ? (
                                        <p className="text-yellow-400 text-xs">No phones in this audience.</p>
                                    ) : (
                                        <div className="max-h-32 overflow-y-auto flex flex-wrap gap-x-3 gap-y-1.5">
                                            {audPhones.slice(0, 200).map((p) => (
                                                <button
                                                    type="button"
                                                    key={p}
                                                    onClick={() => setProfilePhone(p)}
                                                    className="font-mono text-[11px] text-white/80 hover:text-electric underline-offset-2 hover:underline"
                                                    data-testid={`aud-phone-${p}`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                            {audPhones.length > 200 && (
                                                <span className="text-white/40 text-[11px]">+{audPhones.length - 200} more…</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            <Label className="text-white/70">Schedule (EAT, optional)</Label>
                            <Input type="datetime-local" value={form.scheduled_at}
                                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                                className="bg-subtle border-border text-white"
                                data-testid="campaign-schedule" />
                            <p className="text-white/40 text-xs mt-1">Leave blank to save as draft; set future time for auto-dispatch.</p>
                        </div>
                    </div>

                    <DialogFooter className="gap-2 flex-wrap">
                        <Button variant="ghost" onClick={() => setShowDialog(false)} className="text-white/60">Cancel</Button>
                        {audMode === "segment" && audPhones.length > 0 && !editingId && (
                            <Button
                                onClick={handleSaveAndSchedule}
                                disabled={audLoading}
                                variant="outline"
                                className="border-electric/40 text-electric hover:bg-electric/10"
                                data-testid="save-and-schedule-btn"
                            >
                                <Rocket className="h-4 w-4 mr-2" />
                                Save base &amp; {form.scheduled_at ? "schedule" : "create"}
                            </Button>
                        )}
                        <Button onClick={handleSave} className="bg-electric hover:bg-vodaredDark text-white" data-testid="save-campaign-btn">
                            {editingId ? "Update" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CustomerProfileSheet
                phone={profilePhone}
                open={!!profilePhone}
                onOpenChange={(o) => { if (!o) setProfilePhone(null); }}
            />
        </AdminLayout>
    );
};

const SegmentTile = ({ id, current, onClick, icon: Icon, label, hint }) => {
    const active = current === id;
    return (
        <button
            type="button"
            onClick={() => onClick(id)}
            data-testid={`segment-tile-${id}`}
            className={`text-left p-3 rounded-sm border transition-colors ${
                active
                    ? "border-electric bg-electric/10"
                    : "border-border bg-subtle hover:border-white/30"
            }`}
        >
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-3.5 w-3.5 ${active ? "text-electric" : "text-white/60"}`} />
                <span className={`text-xs font-bold ${active ? "text-electric" : "text-white"}`}>{label}</span>
            </div>
            <p className="text-[10px] text-white/50">{hint}</p>
        </button>
    );
};

const PendingCard = ({ c, idx, onApprove, onReject, onEdit }) => {
    const scheduled = c.scheduled_at ? new Date(c.scheduled_at).toLocaleString("en-GB", {
        timeZone: "Africa/Dar_es_Salaam", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
    }) + " EAT" : null;
    return (
        <div className="bg-paper border border-yellow-500/20 rounded p-3" data-testid={`pending-card-${idx}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="text-white font-heading font-bold text-sm truncate">{c.name}</div>
                    <div className="text-white/70 text-xs mt-1 font-mono bg-obsidian/50 p-2 rounded">
                        {c.message_template}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-white/50">
                        <span><Clock className="inline h-3 w-3 mr-0.5" /> Dispatch: {scheduled || "manual"}</span>
                        <span><FileText className="inline h-3 w-3 mr-0.5" /> {c.message_template.length} chars</span>
                        {c.recipient_count > 0 && (
                            <span>{c.customer_base_name}: {c.recipient_count} nos.</span>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" onClick={onApprove} className="bg-electric hover:bg-vodaredDark text-white" data-testid={`approve-${idx}`}>
                        <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={onEdit} className="border-border text-white/70 hover:bg-subtle" data-testid={`edit-pending-${idx}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={onReject} className="text-white/50 hover:text-red-400" data-testid={`reject-${idx}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default CampaignManager;
